import Icon from '@components/Icon/Icon';
import { useMusicState } from '@hooks/storeHooks/useMusicState';
import { useStationMode } from '@hooks/station';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface VolumeControlProps {
  volume: number; // 0 to 1
  onVolumeChange: (volume: number) => void;
  className?: string;
  size?: number;
  /** Station-only: which way the slider panel pops out. 'left' suits the full
   * player's right-edge icon stack; 'top' suits the mini bottom bar. */
  stationSliderPlacement?: 'left' | 'top';
}

export const VolumeControl: React.FC<VolumeControlProps> = ({
  volume,
  onVolumeChange,
  className = '',
  size = 20,
  stationSliderPlacement = 'left',
}) => {
  const { t } = useTranslation();
  const { preMuteVolume, setPreMuteVolume } = useMusicState();
  const isStationMode = useStationMode();
  const [showSlider, setShowSlider] = useState(false);
  const [localVolume, setLocalVolume] = useState(volume);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isDraggingRef = useRef(false);
  const isTouchEventRef = useRef(false);

  // Update local volume when prop changes (but not while dragging)
  // Also track pre-mute volume in Redux for persistence across sessions
  useEffect(() => {
    if (!isDraggingRef.current) {
      // Update preMuteVolume in Redux when volume is non-zero (user adjusting volume)
      if (volume > 0) {
        setPreMuteVolume(volume);
      }
      setLocalVolume(volume);
    }
  }, [volume, setPreMuteVolume]);

  // Determine which volume icon to show based on volume level
  const getVolumeIcon = useCallback(() => {
    if (volume === 0) return 'volume-0';
    if (volume <= 0.33) return 'volume-1';
    if (volume <= 0.66) return 'volume-2';
    return 'volume-3';
  }, [volume]);

  const handleVolumeClick = useCallback(() => {
    if (isStationMode && isTouchEventRef.current) {
      isTouchEventRef.current = false;
      return;
    }

    if (isStationMode) {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
      setShowSlider((prev) => !prev);
      return;
    }

    if (volume === 0) {
      const volumeToRestore = preMuteVolume > 0 ? preMuteVolume : 1;
      onVolumeChange(volumeToRestore);
    } else {
      setPreMuteVolume(volume);
      onVolumeChange(0);
    }
  }, [volume, preMuteVolume, onVolumeChange, setPreMuteVolume, isStationMode]);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (isStationMode) {
        e.preventDefault();
        isTouchEventRef.current = true;
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
          hideTimeoutRef.current = null;
        }
        setShowSlider((prev) => !prev);
      }
    },
    [isStationMode],
  );

  // Handle slider change
  const handleSliderChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newVolume = parseFloat(event.target.value);
      setLocalVolume(newVolume);

      // Update preMuteVolume in Redux when user manually adjusts slider (only if not muting)
      if (newVolume > 0) {
        setPreMuteVolume(newVolume);
      }

      onVolumeChange(newVolume);

      // Dispatch custom event for companion page to listen to
      window.dispatchEvent(
        new CustomEvent('musicVolumeChange', {
          detail: { volume: newVolume },
        }),
      );
    },
    [onVolumeChange, setPreMuteVolume],
  );

  // Handle mouse down on slider - start dragging
  const handleMouseDown = useCallback(() => {
    isDraggingRef.current = true;
  }, []);

  // Handle mouse up on slider - stop dragging
  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false;
  }, []);

  // Handle mouse enter - show slider immediately
  const handleMouseEnter = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setShowSlider(true);
  }, []);

  // Handle mouse leave - hide slider with delay
  const handleMouseLeave = useCallback(() => {
    hideTimeoutRef.current = setTimeout(() => {
      setShowSlider(false);
    }, 150); // Small delay to allow moving to slider
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  // Station kiosk uses a larger, touch-friendly tap target and icon.
  const iconButtonSizeClass = isStationMode
    ? 'w-[56px] h-[56px]'
    : 'w-[40px] h-[40px]';
  const effectiveIconSize = isStationMode ? 30 : size;

  return (
    <div className={`relative ${className}`}>
      {/* Volume Icon Button */}
      <button
        onClick={handleVolumeClick}
        {...(isStationMode && { onTouchStart: handleTouchStart })}
        className={`flex ${iconButtonSizeClass} rounded-full items-center justify-center hover:bg-white/10 transition-colors`}
        title={t('player.volume', { value: Math.round(volume * 100) })}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}>
        <Icon name={getVolumeIcon()} size={effectiveIconSize} color="#FCFCFC" />
      </button>

      {/* Volume Slider - Desktop appears on hover (above icon). In Station mode it's a
          larger, touch-friendly panel that pops out to the left of the icon. */}
      {showSlider && (
        <div
          className={`absolute bg-black/90 backdrop-blur-sm rounded-lg z-50 overflow-hidden ${
            isStationMode
              ? stationSliderPlacement === 'top'
                ? 'bottom-full left-1/2 -translate-x-1/2 mb-3 p-4 w-[88px] h-[240px] block'
                : 'right-full top-1/2 -translate-y-1/2 mr-3 p-4 w-[88px] h-[240px] block'
              : 'bottom-full left-1/2 -translate-x-1/2 mb-2 p-3 w-[60px] h-[120px] hidden sm:block'
          }`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}>
          <div className="flex flex-col items-center justify-center gap-2 h-full">
            <div
              className={`relative w-full flex items-center justify-center ${
                isStationMode ? 'h-[180px]' : 'h-[60px]'
              }`}>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={localVolume}
                onChange={handleSliderChange}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onTouchStart={handleMouseDown}
                onTouchEnd={handleMouseUp}
                className={`bg-white/20 rounded-lg appearance-none cursor-pointer
                                         [&::-webkit-slider-thumb]:appearance-none
                                         [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:cursor-pointer
                                         [&::-webkit-slider-thumb]:shadow-md
                                         [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:cursor-pointer
                                         [&::-moz-range-thumb]:border-none [&::-moz-range-thumb]:shadow-md ${
                                           isStationMode
                                             ? 'w-[180px] h-3 [&::-webkit-slider-thumb]:w-7 [&::-webkit-slider-thumb]:h-7 [&::-moz-range-thumb]:w-7 [&::-moz-range-thumb]:h-7'
                                             : 'w-[60px] h-2 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4'
                                         }`}
                style={{
                  background: `linear-gradient(to right, #FCFCFC 0%, #FCFCFC ${
                    localVolume * 100
                  }%, rgba(255,255,255,0.2) ${
                    localVolume * 100
                  }%, rgba(255,255,255,0.2) 100%)`,
                  transform: 'rotate(-90deg)',
                  transformOrigin: 'center',
                }}
              />
            </div>
            <span
              className={`text-white font-medium ${
                isStationMode ? 'text-base' : 'text-xs'
              }`}>
              {Math.round(localVolume * 100)}%
            </span>
          </div>
          {/* Pointer arrow: right (station 'left' placement, toward icon) or down (otherwise, below panel) */}
          {isStationMode && stationSliderPlacement === 'left' ? (
            <div className="absolute top-1/2 left-full -translate-y-1/2 w-0 h-0 border-t-4 border-b-4 border-l-4 border-transparent border-l-black/90"></div>
          ) : (
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black/90"></div>
          )}
        </div>
      )}
    </div>
  );
};
